import { trackMiscAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import { isVoiceSupported, MAX_RECORDING_MS, useVoiceRecorder } from 'hooks/useVoiceRecorder';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import * as S from './styles';

// Dictation control for chat and creator inputs. Idle it's a borderless mic in
// the action row; recording covers the prompt box with that surface's
// focused-input look — timer, live waveform, X to discard, ✓ to accept, all
// bottom-aligned where the action row lives. The recording controls mirror the
// resting ones: X carries the mic button's chrome, ✓ is the submit button with
// a check glyph, so accepting lands on the same spot as sending.

const LEVEL_SAMPLE_MS = 80;
const BAR_MIN_PX = 3;
const BAR_MAX_PX = 24;
const BAR_WIDTH_PX = 3;
const BAR_GAP_PX = 3;
// deep enough to fill the widest prompt at 6px per bar
const MAX_LEVEL_HISTORY = 400;

// compensates the submit arrow's optical-centering wrapper so the mic reads as
// vertically centered against it
const MIC_NUDGE_STYLE = { transform: 'translateY(1px)' };

// the timer flips to a countdown this close to the recording cap, so the
// auto-finish is telegraphed rather than a surprise
const COUNTDOWN_THRESHOLD_MS = 30_000;

interface VoiceInputButtonProps {
	// final transcript for a completed recording
	onTranscript: (text: string) => void;
	// lets the host capture what was typed before recording began
	onRecordStart: () => void;
	// the creator input carries its own rounding and focus glow
	isCreator?: boolean;
}

type AnalyserRef = MutableRefObject<AnalyserNode | null>;

// rolling history of RMS levels; newest sample enters from the right, and the
// visible window stretches to however many bars the container fits
const ScrollingWaveform = ({ analyserRef }: { analyserRef: AnalyserRef }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [levels, setLevels] = useState<number[]>([]);
	const [barCount, setBarCount] = useState(48);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const update = () =>
			setBarCount(
				Math.max(8, Math.floor((container.clientWidth + BAR_GAP_PX) / (BAR_WIDTH_PX + BAR_GAP_PX)))
			);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const buffer = new Uint8Array(analyserRef.current?.fftSize ?? 1024);
		const ticker = window.setInterval(() => {
			const analyser = analyserRef.current;
			if (!analyser) return;
			analyser.getByteTimeDomainData(buffer);
			let sum = 0;
			for (let i = 0; i < buffer.length; i++) {
				const v = (buffer[i] - 128) / 128;
				sum += v * v;
			}
			const rms = Math.sqrt(sum / buffer.length);
			setLevels((prev) => [...prev.slice(-(MAX_LEVEL_HISTORY - 1)), rms]);
		}, LEVEL_SAMPLE_MS);
		return () => window.clearInterval(ticker);
	}, [analyserRef]);

	return (
		<S.Waveform ref={containerRef}>
			{levels.slice(-barCount).map((level, i) => (
				<S.LevelBar
					key={i}
					style={{
						height: `${BAR_MIN_PX + Math.min(1, level * 5) * (BAR_MAX_PX - BAR_MIN_PX)}px`,
					}}
				/>
			))}
		</S.Waveform>
	);
};

const VoiceInputButton = ({ onTranscript, onRecordStart, isCreator }: VoiceInputButtonProps) => {
	const theme = useTheme();

	const onTranscriptWithTracking = useCallback(
		(text: string) => {
			trackMiscAction('voice_transcribe');
			onTranscript(text);
		},
		[onTranscript]
	);

	const { state, elapsedMs, error, analyserRef, start, stop, cancel } = useVoiceRecorder({
		onTranscript: onTranscriptWithTracking,
		onRecordStart,
	});

	const [isErrorVisible, setIsErrorVisible] = useState(false);
	useEffect(() => {
		if (!error) return;
		setIsErrorVisible(true);
		const timer = window.setTimeout(() => setIsErrorVisible(false), 3500);
		return () => window.clearTimeout(timer);
	}, [error]);

	if (!isVoiceSupported()) return null;

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// counts up while recording, flipping to time-remaining near the cap
	const remainingMs = MAX_RECORDING_MS - elapsedMs;
	const isCountdown = remainingMs <= COUNTDOWN_THRESHOLD_MS;
	const shownSeconds = isCountdown
		? Math.max(0, Math.ceil(remainingMs / 1000))
		: Math.floor(elapsedMs / 1000);
	const clock = `${Math.floor(shownSeconds / 60)}:${String(shownSeconds % 60).padStart(2, '0')}`;
	const timerLabel = isCountdown ? `${clock} left` : clock;

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderCancelButton = () => (
		<S.MicButton onClick={cancel} title="Cancel" style={MIC_NUDGE_STYLE}>
			<Icon type={IconType.CLOSE} width={16} height={16} color={theme.gray[1000]} />
		</S.MicButton>
	);

	const renderTakeoverOverlay = () => (
		<S.TakeoverOverlay isCreator={isCreator}>
			{state === 'recording' ? (
				<S.Row>
					<S.Timer>{timerLabel}</S.Timer>
					<ScrollingWaveform analyserRef={analyserRef} />
					{renderCancelButton()}
					<S.SubmitMirrorWrapper>
						<SubmitButton
							state={SubmitButtonState.REGULAR}
							type={IconType.CHECK}
							tooltip="Accept"
							tooltipPlacement="bottom-start"
							onClick={stop}
						/>
					</S.SubmitMirrorWrapper>
				</S.Row>
			) : (
				<S.Row>
					<S.Spinner />
					<S.TakeoverLabel>Transcribing…</S.TakeoverLabel>
					<S.Spacer />
					{renderCancelButton()}
				</S.Row>
			)}
		</S.TakeoverOverlay>
	);

	return (
		<S.Base>
			{isErrorVisible && error && <S.ErrorPill>{error}</S.ErrorPill>}
			<S.MicButton
				onClick={() => void start()}
				title="Dictate"
				style={MIC_NUDGE_STYLE}
				data-testid="voice-input-button"
			>
				<Icon type={IconType.MICROPHONE} width={16} height={16} color={theme.gray[1000]} />
			</S.MicButton>
			{state !== 'idle' && renderTakeoverOverlay()}
		</S.Base>
	);
};

export default VoiceInputButton;
